import { precioACentavos } from './compras'

// Reglas del módulo Cuentas por Pagar (funciones puras).
//
// Los saldos, abonos y estados financieros los calcula Supabase
// (public.vw_cuentas_por_pagar); aquí solo se mapean a filas de pantalla y se
// validan formularios. NUNCA se recalculan desde Gastos. Montos en CENTAVOS
// enteros para evitar errores de coma flotante.

export const MEDIOS_PAGO = [
  { valor: 'transferencia', etiqueta: 'Transferencia' },
  { valor: 'efectivo', etiqueta: 'Efectivo' },
  { valor: 'cheque', etiqueta: 'Cheque' },
  { valor: 'tarjeta_credito', etiqueta: 'Tarjeta de crédito' },
  { valor: 'tarjeta_debito', etiqueta: 'Tarjeta de débito' },
  { valor: 'otro', etiqueta: 'Otro' },
]

export const TIPOS_RESPALDO = [
  { valor: 'FACTURA', etiqueta: 'Factura' },
  { valor: 'BOLETA', etiqueta: 'Boleta' },
  { valor: 'RECIBO', etiqueta: 'Recibo' },
  { valor: 'LIQUIDACION_COMPRA', etiqueta: 'Liquidación de compra' },
  { valor: 'OTRO', etiqueta: 'Otro' },
  { valor: 'SIN_DOCUMENTO', etiqueta: 'Sin documento' },
  { valor: 'DOCUMENTO_PENDIENTE', etiqueta: 'Documento pendiente' },
]

const RESPALDOS_SIN_NUMERO = ['SIN_DOCUMENTO', 'DOCUMENTO_PENDIENTE']

// Límites reales de pagos_compras.
export const LIMITE_REFERENCIA = 160
export const LIMITE_OBSERVACIONES = 1000
export const LIMITE_NUMERO_DOCUMENTO = 100
export const MOTIVO_ANULACION_MIN = 5
export const MOTIVO_ANULACION_MAX = 500

export function etiquetaMedioPago(valor) {
  return MEDIOS_PAGO.find((medio) => medio.valor === valor)?.etiqueta || '—'
}

export function etiquetaRespaldo(valor) {
  return (
    TIPOS_RESPALDO.find((respaldo) => respaldo.valor === valor)?.etiqueta ||
    '—'
  )
}

export const ESTADOS_FINANCIEROS = {
  SIN_PAGOS_REGISTRADOS: {
    etiqueta: 'Sin pagos registrados',
    clase: 'bg-slate-100 text-slate-600',
  },
  PARCIAL: {
    etiqueta: 'Parcial',
    clase: 'bg-sky-100 text-sky-700',
  },
  PAGADA: {
    etiqueta: 'Pagada',
    clase: 'bg-emerald-100 text-emerald-700',
  },
  PROVISIONAL: {
    etiqueta: 'Provisional',
    clase: 'bg-amber-100 text-amber-700',
  },
  COMPROMISO_ESTIMADO: {
    etiqueta: 'Compromiso estimado',
    clase: 'bg-violet-100 text-violet-700',
  },
}

export const ESTADO_VENCIDA = {
  etiqueta: 'Vencida',
  clase: 'bg-red-100 text-red-700',
}

export const ESTADOS_PAGO = {
  REGISTRADO: { etiqueta: 'Registrado', clase: 'bg-emerald-100 text-emerald-700' },
  ANULADO: { etiqueta: 'Anulado', clase: 'bg-red-50 text-red-600' },
}

// ---------- Permisos (la autoridad final es Supabase) ----------

const ROLES_REGISTRAR_PAGO = ['administrador', 'aprobador']

export function puedeRegistrarPagoCompra(rol) {
  return ROLES_REGISTRAR_PAGO.includes(rol)
}

export function puedeAsociarDocumentoPago(rol) {
  return ROLES_REGISTRAR_PAGO.includes(rol)
}

export function puedeAnularPagoCompra(rol) {
  return rol === 'administrador'
}

// Solo compras con deuda confirmada (recepción validada) y saldo.
export function filaAdmitePago(fila) {
  return (
    Boolean(fila) &&
    fila.clasificacion.tipo === 'OBLIGACION' &&
    fila.saldoCentavos > 0
  )
}

// ---------- Montos ----------

export function pesosACentavos(valor) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return 0

  return Math.round(numero * 100)
}

// ---------- Mapeo de vw_cuentas_por_pagar ----------

// Convierte una fila de la vista en la fila de pantalla. No calcula saldos.
export function construirFilaDesdeVista(registro) {
  const estado = registro.estado_financiero
  const tipo =
    estado === 'COMPROMISO_ESTIMADO'
      ? 'COMPROMISO'
      : registro.deuda_confirmada
        ? 'OBLIGACION'
        : 'PROVISIONAL'

  const totalCentavos = pesosACentavos(
    tipo === 'COMPROMISO'
      ? registro.compromiso_estimado
      : tipo === 'OBLIGACION'
        ? registro.monto_obligacion
        : registro.monto_logistico_compra
  )

  const esObligacion = tipo === 'OBLIGACION'

  return {
    compra: {
      id: registro.compra_id,
      codigo_compra: registro.codigo_compra,
      numero_compra: registro.numero_compra,
      fecha_compra: registro.fecha_compra,
      proveedor_id: registro.proveedor_id,
      proveedor_razon_social: registro.proveedor_razon_social,
      proveedor_nombre_fantasia: registro.proveedor_nombre,
      modalidad: registro.modalidad,
      estado: registro.estado_compra,
      condicion_pago: registro.condicion_pago,
      plazo_dias: registro.plazo_dias,
    },
    estadoRecepcion: registro.estado_recepcion || null,
    clasificacion: { tipo, definitivo: esObligacion },
    estado,
    totalCentavos,
    abonadoCentavos: esObligacion
      ? pesosACentavos(registro.monto_abonado)
      : null,
    saldoCentavos: esObligacion
      ? pesosACentavos(registro.saldo_pendiente)
      : null,
    cantidadPagos: Number(registro.cantidad_pagos) || 0,
    cantidadPagosAnulados: Number(registro.cantidad_pagos_anulados) || 0,
    ultimoPago: registro.ultimo_pago || null,
    vencimiento: registro.fecha_vencimiento || null,
    vencida: Boolean(registro.deuda_vencida),
    vencimientoSuperado:
      Boolean(registro.vencimiento_superado) &&
      tipo !== 'COMPROMISO' &&
      estado !== 'PAGADA',
    factura: registro.factura_id
      ? {
          id: registro.factura_id,
          numero: registro.factura_numero,
          tipo: registro.factura_tipo,
          montoCentavos: pesosACentavos(registro.monto_factura),
          diferenciaCentavos:
            registro.diferencia_factura_vs_compras === null ||
            registro.diferencia_factura_vs_compras === undefined
              ? null
              : pesosACentavos(registro.diferencia_factura_vs_compras),
          gastoId: registro.gasto_id || null,
          estadoGasto: registro.estado_gasto_factura || null,
        }
      : null,
    requiereConciliacionFactura: Boolean(
      registro.requiere_conciliacion_factura
    ),
  }
}

// ---------- Filtros y resumen ----------

export function filtrarCuentas(filas, filtros = {}) {
  const {
    proveedorId = '',
    desde = '',
    hasta = '',
    modalidad = '',
    estado = '',
    soloVencidas = false,
  } = filtros

  return filas.filter((fila) => {
    const { compra } = fila

    if (proveedorId && compra.proveedor_id !== proveedorId) return false
    if (desde && compra.fecha_compra < desde) return false
    if (hasta && compra.fecha_compra > hasta) return false
    if (modalidad && compra.modalidad !== modalidad) return false
    if (estado && fila.estado !== estado) return false
    if (soloVencidas && !fila.vencida) return false

    return true
  })
}

// Indicadores del dashboard sobre las filas ya filtradas. Solo la deuda
// confirmada suma; provisionales y compromisos se informan aparte.
export function resumirCuentas(filas) {
  const obligaciones = filas.filter(
    (fila) => fila.clasificacion.tipo === 'OBLIGACION'
  )
  const compromisos = filas.filter(
    (fila) => fila.clasificacion.tipo === 'COMPROMISO'
  )
  const provisionales = filas.filter(
    (fila) => fila.clasificacion.tipo === 'PROVISIONAL'
  )

  const suma = (lista, campo) =>
    lista.reduce((total, fila) => total + fila[campo], 0)

  return {
    cantidadObligaciones: obligaciones.length,
    totalCentavos: suma(obligaciones, 'totalCentavos'),
    abonadoCentavos: suma(obligaciones, 'abonadoCentavos'),
    pendienteCentavos: suma(obligaciones, 'saldoCentavos'),
    vencidoCentavos: suma(
      obligaciones.filter((fila) => fila.vencida),
      'saldoCentavos'
    ),
    cantidadVencidas: obligaciones.filter((fila) => fila.vencida).length,
    cantidadCompromisos: compromisos.length,
    compromisosCentavos: suma(compromisos, 'totalCentavos'),
    cantidadProvisionales: provisionales.length,
    provisionalesCentavos: suma(provisionales, 'totalCentavos'),
  }
}

// ---------- Formularios ----------

export function respaldoRequiereNumero(tipoRespaldo) {
  return Boolean(tipoRespaldo) && !RESPALDOS_SIN_NUMERO.includes(tipoRespaldo)
}

function validarRespaldo(tipoRespaldo, numeroDocumento, errores) {
  if (!TIPOS_RESPALDO.some((respaldo) => respaldo.valor === tipoRespaldo)) {
    errores.tipoRespaldo = 'Selecciona el tipo de respaldo.'
  }

  const numero = String(numeroDocumento || '').trim()

  if (RESPALDOS_SIN_NUMERO.includes(tipoRespaldo) && numero) {
    errores.numeroDocumento =
      'Este tipo de respaldo no lleva número de documento.'
  } else if (numero.length > LIMITE_NUMERO_DOCUMENTO) {
    errores.numeroDocumento = `El número admite hasta ${LIMITE_NUMERO_DOCUMENTO} caracteres.`
  }
}

// Valida el pago con las mismas reglas que registrar_pago_compra. El
// documento es opcional. No hay anticipos: el monto no puede superar el
// saldo y la fecha debe estar entre la fecha de compra y hoy.
export function validarPagoCompra(
  formulario,
  { saldoCentavos, hoy, fechaCompra }
) {
  const errores = {}

  if (!formulario.fecha) {
    errores.fecha = 'Ingresa la fecha del pago.'
  } else if (hoy && formulario.fecha > hoy) {
    errores.fecha = 'La fecha del pago no puede ser futura.'
  } else if (fechaCompra && formulario.fecha < fechaCompra) {
    errores.fecha = 'La fecha del pago no puede ser anterior a la compra.'
  }

  const montoCentavos = precioACentavos(formulario.monto)

  if (!String(formulario.monto ?? '').trim()) {
    errores.monto = 'Ingresa el monto del pago.'
  } else if (!Number.isFinite(montoCentavos) || montoCentavos <= 0) {
    errores.monto = 'El monto debe ser mayor a cero (máximo 2 decimales).'
  } else if (saldoCentavos === null || saldoCentavos === undefined) {
    errores.monto = 'Esta compra no tiene saldo pagable.'
  } else if (montoCentavos > saldoCentavos) {
    errores.monto = 'El monto no puede superar el saldo pendiente.'
  }

  if (!MEDIOS_PAGO.some((medio) => medio.valor === formulario.medioPago)) {
    errores.medioPago = 'Selecciona el medio de pago.'
  }

  if (String(formulario.referencia || '').trim().length > LIMITE_REFERENCIA) {
    errores.referencia = `La referencia admite hasta ${LIMITE_REFERENCIA} caracteres.`
  }

  validarRespaldo(
    formulario.tipoRespaldo,
    formulario.numeroDocumento,
    errores
  )

  if (
    String(formulario.observaciones || '').trim().length >
    LIMITE_OBSERVACIONES
  ) {
    errores.observaciones = `Las observaciones admiten hasta ${LIMITE_OBSERVACIONES} caracteres.`
  }

  return {
    errores,
    valido: Object.keys(errores).length === 0,
    montoCentavos: Number.isFinite(montoCentavos) ? montoCentavos : null,
  }
}

export function validarDocumentoPago(formulario) {
  const errores = {}

  validarRespaldo(
    formulario.tipoRespaldo,
    formulario.numeroDocumento,
    errores
  )

  return { errores, valido: Object.keys(errores).length === 0 }
}

export function validarMotivoAnulacion(motivo) {
  const largo = String(motivo || '').trim().length

  if (largo < MOTIVO_ANULACION_MIN || largo > MOTIVO_ANULACION_MAX) {
    return `El motivo debe tener entre ${MOTIVO_ANULACION_MIN} y ${MOTIVO_ANULACION_MAX} caracteres.`
  }

  return ''
}

// Un error con `code` fue devuelto por Postgres/PostgREST: la operación se
// rechazó (y revirtió) con certeza. Sin `code` (red, timeout) el resultado
// es DESCONOCIDO y no debe generarse una clave nueva ni repetirse a ciegas.
export function esErrorDefinitivo(error) {
  return Boolean(error?.code)
}
